import { DateTime } from 'luxon';

import type {
  CounselorClass,
  CounselorYouth,
  UpdateBulkMeritBadgeRequirements,
} from '@appTypes/esb';
import type { NormalizedMeritBadge } from '@dict/duck/hooks';
import { isNotUpdateableRequirement } from '@modules/bulkEntry/duck/utils/requirementUtils';
import {
  filterMeritBadgeVersions,
  getLatestActiveVersion,
} from '@modules/bulkEntry/duck/utils/versions';
import type {
  IneligibleReason,
  MBVersion,
} from '@modules/meritBadgeCounselors/mbcBulkEntry/duck/types';
import { validateYouthEligibility } from '@modules/meritBadgeCounselors/mbcBulkEntry/duck/validations';
import { formatToAPIDate } from '@modules/utils/datesLuxon';
import { getPreferredName } from '@shared/utils/preferredName';

import type {
  ClassEntryFormValues,
  ClassRequirement,
  ClassYouth,
  MBClass,
  RequirementEntry,
} from './types';

export type YouthIneligibility =
  | { reason: IneligibleReason; startedVersion?: number }
  | { reason: 'noUserId' };

export const isFutureDate = (date: DateTime) => date > DateTime.now();

export const requirementKey = (req: ClassRequirement) => String(req.id);

export const updateableRequirements = (requirements: ClassRequirement[]) =>
  requirements.filter(req => !isNotUpdateableRequirement(req));

export const effectiveDate = (
  entry: RequirementEntry | undefined,
  completionDate: DateTime | null | undefined,
) => entry?.date ?? completionDate ?? null;

export function toClassOptions(
  classes: CounselorClass[],
  meritBadgesById: Map<number, NormalizedMeritBadge>,
  versionLabel: (version: MBVersion) => string,
): MBClass[] {
  return classes.map(counselorClass => {
    // scoutbookId, not meritBadgeId: the dict, requirements and save endpoints key off it
    const meritBadgeId = counselorClass.scoutbookId;
    const meritBadge = meritBadgesById.get(meritBadgeId);
    const meritBadgeName = meritBadge?.name ?? counselorClass.meirtBadgeName;
    const versions = filterMeritBadgeVersions(
      meritBadge?.versions ?? [],
    ) as MBVersion[];
    const defaultVersion = getLatestActiveVersion(versions);

    return {
      id: counselorClass.counselorClassId,
      label: `${counselorClass.className} - ${meritBadgeName}`,
      meritBadgeId,
      meritBadgeName,
      imageUrl100: meritBadge?.imageUrl100,
      versions: versions.map(version => ({
        versionId: Number(version.versionId),
        label: versionLabel(version),
      })),
      defaultVersionId: defaultVersion && Number(defaultVersion.versionId),
      scouts: counselorClass.scouts.map(scout => ({
        memberId: scout.scoutMemberId,
        userId: scout.scoutSbUserId ?? null,
        name: getPreferredName(scout),
      })),
    };
  });
}

export function youthIneligibility(
  roster: ClassYouth[],
  profileYouths: CounselorYouth[],
  meritBadgeId: number,
  version: MBVersion | undefined,
) {
  const profileByMemberId = new Map(
    profileYouths.map(youth => [youth.memberId, youth]),
  );
  const result = new Map<number, YouthIneligibility>();

  roster.forEach(youth => {
    const profileYouth = profileByMemberId.get(youth.memberId);
    if (!profileYouth) {
      result.set(youth.memberId, { reason: 'notCounseled' });
      return;
    }

    const validation = validateYouthEligibility(
      profileYouth,
      { meritBadgeId },
      version,
    );
    if (!validation.valid) {
      result.set(youth.memberId, {
        reason: validation.reason,
        startedVersion: validation.startedVersion,
      });
      return;
    }

    if (!youth.userId) {
      result.set(youth.memberId, { reason: 'noUserId' });
    }
  });

  return result;
}

export function buildSavePayload(params: {
  values: ClassEntryFormValues;
  requirements: ClassRequirement[];
  roster: ClassYouth[];
  versionId: number;
  today: DateTime;
}): UpdateBulkMeritBadgeRequirements[] {
  const { values, requirements, roster, versionId, today } = params;
  const userIdByMemberId = new Map(
    roster.map(youth => [youth.memberId, youth.userId]),
  );
  const byUserId = new Map<
    number,
    UpdateBulkMeritBadgeRequirements['requirements']
  >();

  updateableRequirements(requirements).forEach(req => {
    const entry = values.requirements?.[requirementKey(req)];
    const date = effectiveDate(entry, values.completionDate);
    if (!date || !entry?.youth?.length) return;

    const apiDate = formatToAPIDate(date);
    entry.youth.forEach(memberId => {
      const userId = userIdByMemberId.get(memberId);
      if (!userId) return;

      const youthRequirements = byUserId.get(userId) ?? [];
      youthRequirements.push({
        id: Number(req.id),
        started: true,
        completed: true,
        dateCompleted: apiDate,
        counselorApprovedDate: apiDate,
        markedCompletedDate: formatToAPIDate(today),
      });
      byUserId.set(userId, youthRequirements);
    });
  });

  return Array.from(byUserId, ([userId, youthRequirements]) => ({
    userId,
    versionId,
    requirements: youthRequirements,
  }));
}
